import React, { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Pagination, SelectChangeEvent } from "@mui/material";
import {
  useCreateAdminMutation,
  useDeleteAdminMutation,
  useGetAdminsQuery,
} from "@/store/rtk-api/admin.api";
import trashIcon from "@/assets/icons/trash.svg";
import DeleteModal from "@/components/DeleteModal/DeleteModal";
import { AdminRole, IAdmin, IAdminFireBase } from "@/store/models/admin.models";
import { withAdmin } from "@/services/withAdmin";
import styles from "./AdminsPage.module.scss";
import Layout from "@/components/Layout/Layout";
import { Snackbar, Alert } from "@mui/material";
import { AddAdminModal } from "./components/AddAdminModal/AddAdminModal";

const ITEMS_PER_PAGE = 8;

const AdminsPageCmp: React.FC<{ admin: IAdminFireBase }> = ({ admin }) => {
  const [page, setPage] = useState(1);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [isOpenAddAdminModal, setIsOpenAddAdminModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<IAdmin | null>(null);
  const [selectedStatus, setSelectedStatus] = useState(AdminRole.ADMIN);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  const {
    data: adminsData,
    isSuccess,
    refetch,
  } = useGetAdminsQuery({
    page,
    limit: ITEMS_PER_PAGE,
  });

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const [deleteAdmin] = useDeleteAdminMutation();
  const [createAdmin] = useCreateAdminMutation();

  const { reset } = useForm<{ emails: string }>();

  const handleRoleChange = (event: SelectChangeEvent<AdminRole>) => {
    const newRole = event.target.value as AdminRole;
    setSelectedStatus(newRole);
  };

  const handleToggleDeleteModal = (admin: IAdmin) => {
    setSelectedAdmin(admin);
    setIsOpenDeleteModal(true);
  };

  const handleToggleAddAdminModal = () => {
    setIsOpenAddAdminModal((prev) => !prev);
  };

  const handleDeleteAdmin = useCallback(async () => {
    if (selectedAdmin) {
      try {
        await deleteAdmin({ uid: selectedAdmin.uid }).unwrap();

        refetch();

        setSnackbarMessage("Admin successfully deleted");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } catch (error) {
        console.error("Failed to delete admin:", error);
      }
    }
    setIsOpenDeleteModal(false);
  }, [selectedAdmin, deleteAdmin, refetch]);

  const onSubmit = async (data: { emails: string }) => {
    try {
      await createAdmin({
        emails: [data.emails],
        status: selectedStatus,
      });
      setIsOpenAddAdminModal(false);
      refetch();
      reset();
    } catch (error) {
      console.error("Failed to create admin:", error);
    }
  };

  return (
    <Layout admin={admin} pageNav={false}>
      <Button
        sx={{
          textTransform: "capitalize",
          height: "44px",
          width: "174px",
          backgroundColor: "#008000",
          borderRadius: "12px",
          "&:hover": {
            backgroundColor: "#008000",
          },
        }}
        variant="contained"
        onClick={handleToggleAddAdminModal}
      >
        Add admin
      </Button>

      <div className={styles.adminCards}>
        {isSuccess &&
          (() => {
            const currentUserAdmin = adminsData.admins.find(
              (adminItem) => admin.email === adminItem.emails[0].email
            );
            const otherAdmins = adminsData.admins.filter(
              (adminItem) => admin.email !== adminItem.emails[0].email
            );

            return (
              <>
                {currentUserAdmin && (
                  <div className={styles.adminCard} key={currentUserAdmin.uid}>
                    <div className={styles.you}>You</div>
                    <div className={styles.adminLabel}>
                      {currentUserAdmin.status}
                    </div>

                    <div className={styles.adminInfoWrap}>
                      <div className={styles.adminInfo}>
                        <p className={styles.adminName}>
                          {currentUserAdmin.name}
                        </p>
                        <p className={styles.adminEmail}>
                          {currentUserAdmin.emails[0].email}
                        </p>
                      </div>
                    </div>

                    <button className={styles.deleteBtn} disabled>
                      <img
                        className={styles.delete}
                        src={trashIcon}
                        alt="delete"
                        style={{ filter: "grayscale(100%)" }}
                      />
                    </button>
                  </div>
                )}

                {otherAdmins.map((adminItem) => (
                  <div className={styles.adminCard} key={adminItem.uid}>
                    <div className={styles.adminLabel}>{adminItem.status}</div>

                    <div className={styles.adminInfoWrap}>
                      <div className={styles.adminInfo}>
                        <p className={styles.adminName}>{adminItem.name}</p>
                        <p className={styles.adminEmail}>
                          {adminItem.emails[0].email}
                        </p>
                      </div>
                    </div>

                    <button
                      className={styles.deleteBtn}
                      onClick={() => handleToggleDeleteModal(adminItem)}
                    >
                      <img
                        className={styles.delete}
                        src={trashIcon}
                        alt="delete"
                      />
                    </button>
                  </div>
                ))}
              </>
            );
          })()}
      </div>
      {isSuccess && adminsData.total > ITEMS_PER_PAGE && (
        <Pagination
          page={page}
          onChange={handlePageChange}
          count={Math.ceil(adminsData.total / ITEMS_PER_PAGE)}
        />
      )}

      {isOpenDeleteModal && selectedAdmin && (
        <DeleteModal
          onDelete={handleDeleteAdmin}
          isOpen={isOpenDeleteModal}
          setIsOpen={setIsOpenDeleteModal}
          name={selectedAdmin.emails[0].email}
          supportingText="You will be able to designate the member as an administrator again"
        />
      )}

      {isOpenAddAdminModal && (
        <AddAdminModal
          open={isOpenAddAdminModal}
          setOpen={setIsOpenAddAdminModal}
          handleRoleChange={handleRoleChange}
          onSubmit={onSubmit}
          selectedStatus={selectedStatus}
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export const AdminsPage = withAdmin(AdminsPageCmp);
AdminsPageCmp.displayName = "AdminsPageCmp";

